import React, { ReactNode, useMemo } from 'react';
import classNames from 'classnames';

import { createStyles } from 'utils/createStyle';
import { colors } from 'styles/settings';

export const styles = createStyles({
    StyledIndicator: [
        'bg-black',
        {
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: colors.brand,
        },
    ],
    optionStyle: ['cursor-pointer align-middle', {}],
});

export interface RadioItemProps {
    value: string;
    selected: boolean;
    text: ReactNode;
    onClick: (value: string) => void;
    disabled?: boolean;
    disabledRadioOnly?: boolean;
    fontTypeUnselected?: string;
    fontColor?: string;
}

export const RadioItem = ({
    text,
    value,
    selected,
    onClick,
    disabled = false,
    disabledRadioOnly = false,
    fontTypeUnselected = '',
    fontColor = colors.brand,
}: RadioItemProps) => {
    const dynamicStyles = useMemo(() => {
        return createStyles({
            container: [
                'flex flex-row items-center mx-auto text-sm',
                {
                    pointerEvents: disabled ? 'none' : 'inherit',
                    color: disabled && !disabledRadioOnly ? colors.grey4 : fontColor,
                    cursor: disabled ? 'not-allowed' : 'inherit',
                },
            ],
            styledRadio: [
                'align-middle',
                {
                    appearance: 'none',
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: 0,
                    borderRadius: '50%',
                    boxShadow: `inset 0 0 0 2px ${disabled ? colors.grey4 : colors.brand}`,
                    width: 20,
                    height: 20,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',

                    '& ~ &': { marginLeft: 5 },

                    '&:focus': {
                        outline: 'none',
                        boxShadow: `inset 0 0 0 2px ${colors.brandDark}`,
                    },
                },
            ],
        });
    }, [disabled, disabledRadioOnly, fontColor]);

    // NOTE: data-testid used to make it easier to target the button state when testing
    return (
        <div className={dynamicStyles.container}>
            <div>
                <button
                    data-testid={text}
                    disabled={disabled}
                    type="button"
                    className={dynamicStyles.styledRadio}
                    onClick={() => onClick(value)}
                >
                    {selected && <span className={styles.StyledIndicator} />}
                </button>
            </div>
            <span
                className={classNames(
                    styles.optionStyle,
                    'ml-2 w-full',
                    selected ? 'font-semibold' : fontTypeUnselected
                )}
                onClick={() => onClick(value)}
            >
                {text}
            </span>
        </div>
    );
};
